(() => {
  'use strict';

  const page = () => document.querySelector('.nn-goods-page');
  const number = (value, fallback = 0) => {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };
  const escapeHtml = (value) => String(value || '').replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
  })[character]);
  const currencyFormatter = (currency, locale) => new Intl.NumberFormat(locale || 'en-CA', {
    style: 'currency', currency: currency || 'CAD'
  });

  function measureVisibleHeaderStack(root) {
    if (typeof document.elementsFromPoint !== 'function' || window.innerWidth < 1) return 0;
    const x = Math.max(2, Math.round(window.innerWidth / 2));
    let boundary = 2;
    let visibleBottom = 0;

    /* Walk down through the full-width layers currently touching the top of
       the viewport. Goods-page elements are excluded, so hero content cannot
       be mistaken for part of the global header. This works without relying
       on theme-specific class names. */
    for (let pass = 0; pass < 6 && boundary < 320; pass += 1) {
      const elements = document.elementsFromPoint(x, boundary);
      let nextBottom = visibleBottom;

      elements.forEach((element) => {
        if (!element || element === document.body || element === document.documentElement) return;
        if (element === root || element.closest('.nn-goods-page')) return;
        const rect = element.getBoundingClientRect();
        const wideEnough = rect.width >= window.innerWidth * 0.65;
        const crossesBoundary = rect.top <= boundary + 1 && rect.bottom > boundary;
        if (wideEnough && crossesBoundary && rect.bottom > 36 && rect.bottom < 320) {
          nextBottom = Math.max(nextBottom, Math.round(rect.bottom));
        }
      });

      if (nextBottom <= visibleBottom + 1) break;
      visibleBottom = nextBottom;
      boundary = visibleBottom + 1;
    }

    return visibleBottom;
  }

  function setStickyOffset(root) {
    const host = page();
    if (!root) return;
    const fallback = number(root.dataset.stickyOffset, 0);
    const selector = root.dataset.headerSelector || '';
    let offset = fallback;
    let hasNaturalOffset = false;
    const stickyBottoms = [];

    const inspectStickyElement = (element) => {
      if (!element || element === root || element.classList.contains('nn-goods-navigation-section')) return;
      const position = window.getComputedStyle(element).position;
      const rect = element.getBoundingClientRect();
      if ((position === 'sticky' || position === 'fixed') && rect.top <= 1 && rect.bottom > 0 && rect.bottom < 320) {
        stickyBottoms.push(Math.round(rect.bottom));
      }
    };

    /* The Goods navigation is the first section in the page template, so its
       wrapper's document position is normally the exact bottom edge of the
       global header. This is more reliable than inspecting which nested theme
       element owns position: sticky. */
    const wrapper = root.closest('.nn-goods-navigation-section') || root.parentElement;
    if (wrapper) {
      const documentTop = Math.round(wrapper.getBoundingClientRect().top + window.scrollY);
      if (documentTop > 0 && documentTop < 320) {
        offset = documentTop;
        hasNaturalOffset = true;
      }
    }

    /* Fixed global headers are removed from document flow, so their wrapper
       position can be zero. In that case use the configured element's visible
       bottom edge or height as the fallback measurement. */
    if (selector) {
      try {
        const header = document.querySelector(selector);
        if (header) {
          const rect = header.getBoundingClientRect();
          const measuredBottom = Math.round(Math.max(rect.height, rect.bottom));
          if (measuredBottom > 0 && measuredBottom < 320) {
            offset = hasNaturalOffset ? Math.min(offset, measuredBottom) : measuredBottom;
          }

          /* Shopify themes often put position: sticky on a section wrapper
             above the configured header element. Follow that ancestor to its
             actual visible bottom edge. */
          let headerAncestor = header;
          while (headerAncestor && headerAncestor !== document.body) {
            inspectStickyElement(headerAncestor);
            headerAncestor = headerAncestor.parentElement;
          }
        }
      } catch (error) {
        if (!hasNaturalOffset) offset = fallback;
      }
    }

    /* Dawn and Dawn-derived themes add this class to the real pinned section
       after scrolling. Inspect it directly even when the configured selector
       points to an inner custom element. */
    document.querySelectorAll('.shopify-section-header-sticky').forEach(inspectStickyElement);
    if (stickyBottoms.length) offset = Math.min(...stickyBottoms);

    const visibleHeaderBottom = measureVisibleHeaderStack(root);
    if (visibleHeaderBottom > 0) offset = visibleHeaderBottom;

    const value = `${Math.max(0, offset)}px`;
    root.style.setProperty('--nn-goods-sticky-top', value);
    if (host) host.style.setProperty('--nn-goods-sticky-top', value);
  }

  function initNavigation(root) {
    if (!root || root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';
    const wrapper = root.closest('.nn-goods-navigation-section') || root.parentElement;
    const nav = root.querySelector('[data-nn-goods-nav]');
    const toggle = root.querySelector('[data-nn-goods-menu-toggle]');
    const links = nav ? [...nav.querySelectorAll('a[href^="#"]')] : [];

    const syncHeight = () => {
      const height = `${root.offsetHeight}px`;
      if (wrapper) wrapper.style.setProperty('--nn-goods-nav-height', height);
      const host = page();
      if (host) host.style.setProperty('--nn-goods-nav-height', height);
    };

    const updatePosition = () => {
      syncHeight();
      root.classList.add('is-fixed');
    };

    const closeMenu = () => {
      if (!nav || !toggle) return;
      nav.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
      toggle.textContent = toggle.dataset.closedLabel || 'Menu';
    };

    if (toggle && nav) {
      toggle.addEventListener('click', () => {
        const open = nav.classList.toggle('is-open');
        toggle.setAttribute('aria-expanded', String(open));
        toggle.textContent = open ? (toggle.dataset.openLabel || 'Close') : (toggle.dataset.closedLabel || 'Menu');
        window.requestAnimationFrame(() => { syncHeight(); updatePosition(); });
      });
    }

    const scrollToSection = (target) => {
      setStickyOffset(root);
      syncHeight();
      const styleSource = page() || root;
      const globalOffset = number(getComputedStyle(styleSource).getPropertyValue('--nn-goods-sticky-top'), 0);
      const targetTop = target.getBoundingClientRect().top + window.scrollY;
      const destination = Math.max(0, targetTop - globalOffset - root.offsetHeight);
      window.scrollTo({ top: destination, behavior: 'smooth' });
    };

    links.forEach((link) => link.addEventListener('click', (event) => {
      const href = link.getAttribute('href');
      const target = href ? document.querySelector(href) : null;
      if (!target) return;
      event.preventDefault();
      closeMenu();
      window.requestAnimationFrame(() => {
        scrollToSection(target);
        if (window.history && window.history.replaceState) window.history.replaceState(null, '', href);
      });
    }));

    const updateActive = () => {
      const styleSource = page() || root;
      const offset = number(getComputedStyle(styleSource).getPropertyValue('--nn-goods-sticky-top'), 0);
      const marker = window.scrollY + offset + root.offsetHeight + 8;
      let activeId = '';
      let activeTop = -Infinity;
      links.forEach((link) => {
        const target = document.querySelector(link.getAttribute('href'));
        const targetTop = target ? target.getBoundingClientRect().top + window.scrollY : Infinity;
        if (targetTop <= marker && targetTop > activeTop) {
          activeId = link.getAttribute('href');
          activeTop = targetTop;
        }
      });
      links.forEach((link) => {
        const active = link.getAttribute('href') === activeId;
        link.classList.toggle('is-active', active);
        if (active) link.setAttribute('aria-current', 'page'); else link.removeAttribute('aria-current');
      });
    };

    const refresh = () => { setStickyOffset(root); syncHeight(); updatePosition(); updateActive(); };
    const onScroll = () => { setStickyOffset(root); updatePosition(); updateActive(); };
    window.addEventListener('resize', refresh, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    if ('ResizeObserver' in window) {
      root._nnGoodsResizeObserver = new ResizeObserver(() => { syncHeight(); updatePosition(); });
      root._nnGoodsResizeObserver.observe(root);
    }
    refresh();
  }

  function initEstimator(root) {
    if (!root || root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';
    const currency = root.dataset.currency || 'CAD';
    const locale = root.dataset.locale || 'en-CA';
    const money = currencyFormatter(currency, locale);
    const min = number(root.dataset.quantityMin, 25);
    const max = number(root.dataset.quantityMax, 500);
    const step = number(root.dataset.quantityStep, 25);
    const bandTwo = number(root.dataset.bandTwo, 50);
    const bandThree = number(root.dataset.bandThree, 100);
    const bandFour = number(root.dataset.bandFour, 250);
    const longSleeveFee = number(root.dataset.longSleeveFee, 3);
    const secondLocationFee = number(root.dataset.secondLocationFee, 3.25);
    const sampleFee = number(root.dataset.sampleFee, 125);
    const depositPercent = number(root.dataset.depositPercent, 50);
    const screenPrices = {
      1: 0,
      2: number(root.dataset.screenTwo, 1.75),
      3: number(root.dataset.screenThree, 2.75),
      4: number(root.dataset.screenFour, 3.75)
    };

    const quantity = root.querySelector('[data-nn-quantity]');
    const range = root.querySelector('[data-nn-quantity-range]');
    const longQtyInput = root.querySelector('[data-nn-long-quantity]');
    const shortQtyInput = root.querySelector('[data-nn-short-quantity]');
    const screenColours = root.querySelector('[data-nn-screen-colours]');
    const secondLocation = root.querySelector('[data-nn-second-location]');
    const sample = root.querySelector('[data-nn-sample]');
    const linesNode = root.querySelector('[data-nn-estimate-lines]');
    const summaryTitle = root.querySelector('[data-nn-summary-title]');
    const summaryLine = root.querySelector('[data-nn-summary-line]');
    const totalNode = root.querySelector('[data-nn-estimate-total]');
    const unitNode = root.querySelector('[data-nn-unit-price]');
    const designDueNode = root.querySelector('[data-nn-design-due]');
    const depositNode = root.querySelector('[data-nn-production-deposit]');
    const savingNode = root.querySelector('[data-nn-saving-note]');
    const hiddenSummary = root.querySelector('[data-nn-estimate-summary]');
    const hiddenTotal = root.querySelector('[data-nn-estimate-total-input]');

    if (!quantity || !range || !longQtyInput || !shortQtyInput) return;

    const checked = (name) => root.querySelector(`input[data-nn-choice="${name}"]:checked`);
    const card = (input) => input ? input.closest('[data-nn-choice-card]') : null;
    const updateChoiceStates = () => root.querySelectorAll('[data-nn-choice-card]').forEach((node) => {
      const input = node.querySelector('input');
      node.classList.toggle('is-selected', Boolean(input && input.checked));
    });
    const clampQuantity = (value) => Math.max(min, Math.min(Math.max(max, value), Math.round(value / step) * step));
    const bandPrice = (tee, qty) => {
      if (qty < bandTwo) return number(tee.dataset.priceOne);
      if (qty < bandThree) return number(tee.dataset.priceTwo);
      if (qty < bandFour) return number(tee.dataset.priceThree);
      return number(tee.dataset.priceFour);
    };

    const addLine = (label, value) => `<div><dt>${escapeHtml(label)}</dt><dd>${escapeHtml(value)}</dd></div>`;

    function update() {
      let qty = Math.max(min, Math.round(number(quantity.value, min)));
      quantity.value = qty;
      range.value = Math.min(max, qty);
      let longQty = Math.max(0, Math.min(qty, Math.round(number(longQtyInput.value, 0))));
      longQtyInput.value = longQty;
      const shortQty = qty - longQty;
      shortQtyInput.value = shortQty;

      const teeInput = checked('tee');
      const finishInput = checked('finish');
      const decorationInput = checked('decoration');
      if (!teeInput || !finishInput || !decorationInput) return;
      const tee = card(teeInput);
      const finish = card(finishInput);
      const decoration = card(decorationInput);
      const base = bandPrice(tee, qty);
      const finishUnit = number(finish.dataset.unitPrice);
      const decorationCode = decoration.dataset.code || '';
      const colourCount = number(screenColours ? screenColours.value : 1, 1);
      const decorationMin = decorationCode === 'screen' ? (screenPrices[colourCount] || 0) : number(decoration.dataset.unitPrice);
      const decorationMax = decorationCode === 'screen' ? decorationMin : number(decoration.dataset.maxPrice, decorationMin);
      const secondUnit = secondLocation && secondLocation.checked ? secondLocationFee : 0;
      const selectedSampleFee = sample && sample.checked ? sampleFee : 0;
      const designFee = number(finish.dataset.designFee);
      const longSleeveTotal = longQty * longSleeveFee;
      const unit = base + finishUnit + decorationMin + secondUnit;
      const productionSubtotal = unit * qty + longSleeveTotal;
      const projectTotal = productionSubtotal + designFee + selectedSampleFee;
      const productionDeposit = productionSubtotal * (depositPercent / 100);

      if (screenColours) screenColours.disabled = decorationCode !== 'screen';
      summaryTitle.textContent = tee.dataset.name || 'T-shirt programme';
      summaryLine.textContent = `${qty} units · ${shortQty} short / ${longQty} long · ${finish.dataset.name || 'Finish'}`;

      let lines = addLine('Garment base', `${money.format(base)} / unit`);
      if (longQty) lines += addLine(`Long-sleeve upgrade · ${longQty} units`, money.format(longSleeveTotal));
      const decorationLabel = decorationCode === 'screen'
        ? `${decoration.dataset.name} · ${colourCount} colour${colourCount === 1 ? '' : 's'}`
        : decoration.dataset.name;
      let decorationValue = decorationMin ? `${money.format(decorationMin)} / unit` : 'Included';
      if (decorationMax > decorationMin) decorationValue = `${money.format(decorationMin)}–${money.format(decorationMax)} / unit`;
      lines += addLine(decorationLabel, decorationValue);
      if (secondUnit) lines += addLine('Second decoration location', `${money.format(secondUnit)} / unit`);
      if (finishUnit) lines += addLine(`${finish.dataset.name} finishing`, `${money.format(finishUnit)} / unit`);
      if (selectedSampleFee) lines += addLine('Pre-production sample', money.format(selectedSampleFee));
      lines += addLine(`${finish.dataset.name} design development`, `From ${money.format(designFee)}`);
      linesNode.innerHTML = lines;
      totalNode.textContent = money.format(projectTotal);
      unitNode.textContent = `${money.format(productionSubtotal / qty)} effective product unit price before tax and delivery`;
      designDueNode.textContent = `From ${money.format(designFee + selectedSampleFee)}`;
      depositNode.textContent = money.format(productionDeposit);

      let next = null;
      if (qty < bandTwo) next = bandTwo;
      else if (qty < bandThree) next = bandThree;
      else if (qty < bandFour) next = bandFour;
      savingNode.textContent = next
        ? `Add ${next - qty} units to reach the next price band.`
        : (qty > max ? 'A final volume quote will be prepared for this quantity.' : `For more than ${max} units, we will prepare custom volume pricing.`);

      const summary = [
        `Tee: ${tee.dataset.name}`,
        `Quantity: ${qty} (${shortQty} short sleeve, ${longQty} long sleeve)`,
        `Decoration: ${decorationLabel}${decorationMax > decorationMin ? ` at ${money.format(decorationMin)}–${money.format(decorationMax)} per unit` : ''}`,
        `Finish: ${finish.dataset.name}`,
        `Second location: ${secondUnit ? 'Yes' : 'No'}`,
        `Pre-production sample: ${selectedSampleFee ? 'Yes' : 'No'}`,
        `Planning total: ${money.format(projectTotal)}`,
        `Design due before work: from ${money.format(designFee + selectedSampleFee)}`,
        `${depositPercent}% production deposit after design approval: ${money.format(productionDeposit)}`
      ].join('\n');
      if (hiddenSummary) hiddenSummary.value = summary;
      if (hiddenTotal) hiddenTotal.value = money.format(projectTotal);
      updateChoiceStates();
    }

    ['tee', 'decoration', 'finish'].forEach((name) => {
      if (!checked(name)) {
        const first = root.querySelector(`input[data-nn-choice="${name}"]`);
        if (first) first.checked = true;
      }
    });
    root.querySelectorAll('input[data-nn-choice], [data-nn-second-location], [data-nn-sample]').forEach((input) => input.addEventListener('change', update));
    if (screenColours) screenColours.addEventListener('change', update);
    longQtyInput.addEventListener('input', update);
    quantity.addEventListener('input', update);
    range.addEventListener('input', () => { quantity.value = range.value; update(); });
    const minus = root.querySelector('[data-nn-quantity-minus]');
    const plus = root.querySelector('[data-nn-quantity-plus]');
    if (minus) minus.addEventListener('click', () => { quantity.value = Math.max(min, number(quantity.value) - step); update(); });
    if (plus) plus.addEventListener('click', () => { quantity.value = number(quantity.value) + step; update(); });

    document.querySelectorAll('[data-nn-select-tee]').forEach((button) => button.addEventListener('click', () => {
      const target = root.querySelector(`input[data-nn-choice="tee"][value="${CSS.escape(button.dataset.nnSelectTee || '')}"]`);
      if (target) { target.checked = true; update(); root.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
    }));
    update();
  }

  function initAll(scope = document) {
    scope.querySelectorAll('[data-nn-goods-navigation]').forEach(initNavigation);
    scope.querySelectorAll('[data-nn-goods-estimator]').forEach(initEstimator);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
